import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * UI_P3B: SymbolDetailsSection — sticky bottom quote/details for the watchlist.
 *
 * Shows the active symbol's item quote (last/change/percent) plus the current
 * datum OHLCV. Values are formatted by the SSOT numberFormat module; a missing
 * value renders an em dash and is never fabricated (D4/D9).
 */
import { useWidgetI18n } from "../../context/WidgetI18nContext";
import { formatChangeValue, formatPercent, formatPrice, formatVolume, } from "../../utils/numberFormat";
import styles from "../../styles/watchlist-panel.module.css";
const DASH = "—";
export function toSparkPoints(values, width, height) {
    const min = Math.min(...values);
    const max = Math.max(...values);
    const span = max - min || 1;
    return values.map((v, i) => {
        const x = (i / Math.max(values.length - 1, 1)) * width;
        const y = height - ((v - min) / span) * (height - 2) - 1;
        return `${x.toFixed(1)},${y.toFixed(1)}`;
    }).join(" ");
}
function finite(value) {
    return typeof value === "number" && Number.isFinite(value);
}
function metric(value, formatter) {
    return Number.isFinite(value) ? formatter(value) : DASH;
}
export function SymbolDetailsSection({ symbol, item, datum }) {
    const { t } = useWidgetI18n();
    const itemPrice = item?.price;
    const itemChange = item?.change;
    const itemPct = item?.changePercent;
    const last = finite(itemPrice)
        ? formatPrice(itemPrice)
        : datum
            ? metric(datum.close, formatPrice)
            : DASH;
    const change = finite(itemChange) ? formatChangeValue(itemChange) : DASH;
    const pctFallback = datum && datum.open !== 0 && Number.isFinite(datum.open) && Number.isFinite(datum.close)
        ? ((datum.close - datum.open) / Math.abs(datum.open)) * 100
        : null;
    const changePct = finite(itemPct)
        ? formatPercent(itemPct)
        : pctFallback !== null
            ? formatPercent(pctFallback)
            : DASH;
    const sign = finite(itemPct) ? itemPct : finite(itemChange) ? itemChange : pctFallback;
    const changeClass = typeof sign !== "number" || !Number.isFinite(sign)
        ? undefined
        : sign > 0
            ? styles.up
            : sign < 0
                ? styles.down
                : undefined;
    const spark = item?.sparkline && item.sparkline.length > 1 ? item.sparkline : null;
    const metrics = datum
        ? [
            ["open", metric(datum.open, formatPrice)],
            ["high", metric(datum.high, formatPrice)],
            ["low", metric(datum.low, formatPrice)],
            ["close", metric(datum.close, formatPrice)],
            ["volume", metric(datum.volume, formatVolume)],
        ]
        : [];
    return (_jsxs("section", { role: "region", "aria-label": symbol, "data-testid": "watchlist-details", className: styles.details, children: [_jsxs("div", { className: styles.detailsHead, children: [_jsxs("div", { className: styles.detailsSymBlock, children: [_jsx("span", { className: styles.detailsSym, children: symbol }), item?.name ? _jsx("span", { className: styles.detailsName, children: item.name }) : null] }), _jsxs("div", { className: styles.detailsQuote, children: [_jsx("span", { className: styles.detailsLast, children: last }), _jsx("span", { className: `${styles.detailsChg}${changeClass ? ` ${changeClass}` : ""}`, children: change }), _jsx("span", { className: `${styles.detailsPct}${changeClass ? ` ${changeClass}` : ""}`, children: changePct })] }), spark ? (_jsx("svg", { className: styles.spark, width: "64", height: "20", viewBox: "0 0 64 20", "aria-hidden": "true", children: _jsx("polyline", { points: toSparkPoints(spark, 64, 20), fill: "none", stroke: "currentColor", strokeWidth: "1.5", className: changeClass }) })) : null] }), metrics.length > 0 ? (_jsx("div", { className: styles.detailGrid, children: metrics.map(([key, value]) => (_jsxs("div", { className: styles.detailCell, children: [_jsx("span", { className: styles.detailLabel, children: t(`datawindow.${key}`) }), _jsx("span", { className: styles.detailValue, "data-metric": key, children: value })] }, key))) })) : (_jsx("div", { className: styles.detailsEmpty, children: t("widget.noData") }))] }));
}
